import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Select } from '../../base/Select';
import { LoadingWheel } from '../../base/LoadingWheel';
import { useUserContext } from '../../../contexts/UserContext';
import { ITemplateConfiguration } from '../../../types/Template/Template';
import useCustomerTemplateConfigurations from '../../../data/configuration/useCustomerTemplateConfiguration';
import useInstallerTemplateConfigurations from '../../../data/configuration/useInstallerTemplateConfiguration';
import { IAppliance } from '../../../types/Appliance/Appliance';
import { ConfigurationType } from '../../../types/Configuration/Configuration';

interface TemplateSelectOptionsProps {
  templates?: ITemplateConfiguration[];
  hardwareModelId: string;
  noTemplates: boolean;
}

function TemplateSelectOptions({
  templates,
  hardwareModelId,
}: TemplateSelectOptionsProps) {
  const filteredTemplates = useMemo(
    () =>
      templates?.filter((t) => t.template.hardwareModelId === hardwareModelId),
    [templates, hardwareModelId]
  );

  if (
    filteredTemplates !== null &&
    filteredTemplates !== undefined &&
    filteredTemplates.length > 0
  ) {
    return (
      <>
        {filteredTemplates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.template.name}
          </option>
        ))}
      </>
    );
  } else {
    return (
      <option disabled value="-1">
        No Templates Avaliable
      </option>
    );
  }
}

interface ViewTemplateSelectProps {
  selectedAppliance: IAppliance;
  selectedTemplate?: ITemplateConfiguration;
  setSelectedTemplate: Dispatch<
    SetStateAction<ITemplateConfiguration | undefined>
  >;
}

export function ViewTemplateSelect({
  selectedAppliance,
  selectedTemplate,
  setSelectedTemplate,
}: ViewTemplateSelectProps) {
  const { isInstaller, installerId, installerName, organisationName } =
    useUserContext();
  const { templateConfigurations: installerTemplateConfigurations } =
    useInstallerTemplateConfigurations();
  const { templateConfigurations: customerTemplateConfigurations } =
    useCustomerTemplateConfigurations();
  const [noTemplates, setNoTemplates] = useState(false);

  const templatesForModel = useMemo(
    () => [
      ...(installerTemplateConfigurations.data?.filter(
        (t) => t.template.hardwareModelId === selectedAppliance.hardwareModelId
      ) ?? []),
      ...(customerTemplateConfigurations.data?.filter(
        (t) => t.template.hardwareModelId === selectedAppliance.hardwareModelId
      ) ?? []),
    ],
    [
      installerTemplateConfigurations.data,
      customerTemplateConfigurations.data,
      selectedAppliance.hardwareModelId,
    ]
  );

  const installerDefault = useMemo(
    () => templatesForModel?.find((t) => t.template.default === true)!,
    [templatesForModel]
  );

  const handleTemplateChange = (e: FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.value === '-2') {
      setSelectedTemplate({
        id: '-2',
        type: ConfigurationType.template,
        ownerId: '',
        createdOn: '',
        createdBy: '',
        template: {
          hardwareModelId: '',
          hardwareManufacturerId: '',
          hardwareTypeId: '',
          managedBy: '',
          name: '',
          default: false,
          tiles: [],
        },
      });

      return;
    }

    const template = templatesForModel.find(
      (t) => t.id === e.currentTarget.value
    );

    if (template) {
      setSelectedTemplate(template);
    } else {
      console.error('Failed to match template');
    }
  };

  useEffect(() => {
    if (
      (!installerTemplateConfigurations.data &&
        !customerTemplateConfigurations.data) ||
      (!installerTemplateConfigurations.data?.length &&
        !customerTemplateConfigurations.data?.length) ||
      (!installerTemplateConfigurations.data?.filter(
        (t) => t.template.hardwareModelId === selectedAppliance.hardwareModelId
      ).length &&
        !customerTemplateConfigurations.data?.filter(
          (t) =>
            t.template.hardwareModelId === selectedAppliance.hardwareModelId
        ).length)
    ) {
      setNoTemplates(true);
    } else {
      setNoTemplates(false);
    }
  }, [
    installerTemplateConfigurations.data,
    customerTemplateConfigurations.data,
    selectedAppliance.hardwareModelId,
  ]);

  useEffect(() => {
    setSelectedTemplate(installerDefault);
  }, [installerDefault, selectedAppliance.hardwareModelId]);

  if (
    installerTemplateConfigurations.isLoading ||
    customerTemplateConfigurations.isLoading
  ) {
    return (
      <div>
        <LoadingWheel size="sm" />
      </div>
    );
  } else {
    return (
      <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
        <p className="text-sm mb-2 md:mb-0">Template</p>
        <Select
          value={
            noTemplates
              ? '-1'
              : selectedTemplate
                ? selectedTemplate.id
                : installerDefault
                  ? installerDefault.id
                  : '-1'
          }
          onChange={handleTemplateChange}
        >
          {installerId && (
            <optgroup label={`${installerName}'s Templates`}>
              <TemplateSelectOptions
                templates={installerTemplateConfigurations.data}
                hardwareModelId={selectedAppliance.hardwareModelId}
                noTemplates={noTemplates}
              />
            </optgroup>
          )}
          <optgroup label={`${organisationName}'s Templates`}>
            <TemplateSelectOptions
              templates={
                isInstaller && !installerId
                  ? installerTemplateConfigurations.data
                  : customerTemplateConfigurations.data
              }
              hardwareModelId={selectedAppliance.hardwareModelId}
              noTemplates={noTemplates}
            />
          </optgroup>
          {!noTemplates && <option value="-2">No Template</option>}
        </Select>
      </div>
    );
  }
}
